import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AddCreditsDialogService } from '@brand-lolly/app/billing/dialogs/add-credits-dialog/add-credits-dialog.service';
import { CancelSubscriptionDialogService } from '@brand-lolly/app/billing/dialogs/cancel-subscription-dialog/cancel-subscription-dialog.service';
import { CancelSubscriptionFeedback } from '@brand-lolly/app/billing/dialogs/cancel-subscription-dialog/cancel-subscription-dialog.model';
import { BillingErrorsService } from '@brand-lolly/app/billing/services/billing-errors.service';
import { CancelSubscriptionService } from '@brand-lolly/app/billing/services/cancel-subscription.service';
import {
	CancelRequest,
	CreditPack
} from '@brand-lolly/app/billing/types/mutations.types';
import { CatalogService } from '@brand-lolly/app/billing/services/catalog.service';
import { OpenBillingPortalSessionService } from '@brand-lolly/app/billing/services/open-billing-portal-session.service';
import { ReleaseScheduledChangeService } from '@brand-lolly/app/billing/services/release-scheduled-change.service';
import { SubscriptionsStateService } from '@brand-lolly/app/billing/services/subscriptions-state.service';
import { UncancelSubscriptionService } from '@brand-lolly/app/billing/services/uncancel-subscription.service';
import { humanizeAlias } from '@brand-lolly/app/billing/types/alias-labels';
import {
	PlanEntitlementAlias,
	ProductAlias,
	ScheduledChangeType,
	SideUsageAlias,
	SubscriptionPhase,
	SubscriptionStatus
} from '@brand-lolly/app/billing/types/billing.enums';
import { GetCreditBalanceService } from '@brand-lolly/app/billing/services/get-credit-balance.service';
import { GetCreditPacksService } from '@brand-lolly/app/billing/services/get-credit-packs.service';
import { CreditBalance } from '@brand-lolly/app/billing/types/credit-balance.types';
import {
	Product,
	PRODUCT_REGISTRY,
	ProductId
} from '@brand-lolly/app/billing/types/product.types';
import {
	ProductSubscription,
	SideUsage,
	UsageEntitlement,
	UsageLimit
} from '@brand-lolly/app/billing/types/subscription.types';
import { ApiKeyService } from '@brand-lolly/app/billing/services/api-key.service';
import {
	ApiKey,
	DEFAULT_API_SCOPES
} from '@brand-lolly/app/billing/types/api-key.types';
import {
	LoButton,
	LoCard,
	LoEmptyState,
	LoIcon,
	LoMetric,
	LoNotice,
	LoPageHeader,
	LoProgress,
	LoSkeleton,
	LoStatus,
	LoToastService
} from 'lo-design-system';
import { combineLatest, Subject, takeUntil, timer } from 'rxjs';
import { AnaChannelConnectService } from '@brand-lolly/app/_services/ana-channel-connect.service';

interface ProductSubscriptionView {
	productId: ProductId;
	productTitle: string;
	subscription: ProductSubscription;
	actionInFlight: boolean;
}

/**
 * The two global `side_*` renewable counters the credit meter replaced, and the
 * reason this page no longer prints them.
 *
 * `side.messages_per_month` and `side.email_unlocks` are the only renewable
 * limits the SidePlan overlay ever carried, and both of their actions are now
 * metered: `messaging.*` and `profile_enrichment.email_unlock` are published on
 * `GET /billing/credits/prices`, and inclusion in that list IS the metered fact
 * (`app/billing/credits/prices_service.py` — an inert row is never published).
 * `MessagingSendMeter.authorise` reserves credits and never touches the counter,
 * which is what `app/billing/messaging/allowance.py` means when it says a card
 * reading `side.messages_per_month` "would count down a bound nothing enforces".
 *
 * What is NOT retired is the rest of the payload: `side.email_accounts`,
 * `side.instagram_accounts` and `side.tiktok_accounts` are static caps, still
 * enforced by `EnforcerService._side_plan_static_cap`, and deliberately kept out
 * of the credit cut-over (task 7.7). They are deliverability caps — how many
 * senders the inbox may hold — not an allowance of sends, so credits do not
 * replace them.
 *
 * Named rather than dropping the whole `side_limits` loop, so a renewable side
 * limit that is NOT one of these two would still render.
 */
const METERED_SIDE_LIMITS: ReadonlySet<string> = new Set<string>([
	SideUsageAlias.MESSAGES_PER_MONTH,
	SideUsageAlias.EMAIL_UNLOCKS
]);

/**
 * Subscription statuses that actually open a product, mirroring
 * `SubscriptionsStateService`'s own `ACCESS_STATUSES`. `past_due` and `pending`
 * rows come back from `GET /billing` and are refused by the backend's product
 * gate, so they must not read as "holds the Creator Data API".
 */
const API_ACCESS_STATUSES: ReadonlySet<SubscriptionStatus> = new Set([
	SubscriptionStatus.ACTIVE,
	SubscriptionStatus.TRIALING_APP,
	SubscriptionStatus.TRIALING_STRIPE
]);

@Component({
	standalone: true,
	selector: 'app-billing-page',
	templateUrl: './billing-page.component.html',
	styleUrls: ['./billing-page.component.scss'],
	imports: [
		DecimalPipe,
		DatePipe,
		LoButton,
		LoCard,
		LoEmptyState,
		LoIcon,
		LoMetric,
		LoNotice,
		LoPageHeader,
		LoProgress,
		LoSkeleton,
		LoStatus
	]
})
export class BillingPageComponent implements OnInit, OnDestroy {
	loading = false;

	// Placeholder collections driving the loading skeleton grid.
	readonly skeletonCards = [0, 1, 2];
	readonly skeletonRows = [0, 1, 2, 3];
	cards: ProductSubscriptionView[] = [];
	/**
	 * The outreach rows that still enforce something: the static per-channel
	 * account caps, plus any renewable side limit the credit meter has NOT
	 * replaced (see {@link METERED_SIDE_LIMITS}).
	 */
	outreachCaps: UsageEntitlement[] = [];
	outreachLimits: UsageLimit[] = [];
	hasSideUsage = false;
	portalOpening = false;

	// ─── API access ──────────────────────────────────────────────────────
	// "API requests" = the metered AI-analysis allowance the public API debits
	// (alias ai_influencer_analyses_per_month, owned by the Discovery product);
	// we surface the same limit that already rides in the Discovery card.
	apiUsage: UsageLimit | null = null;
	apiRequestsRemaining: number | null = null;
	apiRequestsPercent = 0;
	// Influencer API credit balance — the accumulating (rolling) credit pool the
	// public API debits. Non-null only when the brand has an API subscription;
	// when set it replaces the legacy Discovery allowance block above.
	apiCredits: CreditBalance | null = null;
	// Monthly credit grant (api_credits_grant_per_month) from the API sub, if any.
	apiCreditsGrant: number | null = null;
	// The one active key we display (identifier only — secret is not retrievable).
	activeApiKey: ApiKey | null = null;
	apiKeyMasked = '';
	// Full token, held only in memory right after create/rotate (shown once).
	revealedToken: string | null = null;
	apiKeyLoading = false;
	/** The key list has answered (either way). The card's visibility depends on
	 *  whether a key exists, so it must not be decided before we know. */
	apiKeyLoaded = false;
	apiKeyActionInFlight = false;
	/**
	 * This brand holds an access-granting **Creator Data API** subscription —
	 * the exact gate `POST /api-keys` and `POST /api-keys/{id}/rotate` declare
	 * (`require_active_product_subscription(ProductAlias.CREATOR_DATA_API)`), so
	 * a false here means minting a key answers 402 `no_active_subscription`.
	 *
	 * NOT `subscriptionsState.hasProductAccess(CREATOR_DATA_API)`: that resolves
	 * a unified application subscription as access to any product it has no
	 * `feature.*` row for, and the Creator Data API deliberately has none — it
	 * was never folded into the application. It would answer yes for every
	 * platform subscriber and put the dead button back.
	 */
	hasApiSubscription = false;
	/**
	 * Whether the API-access card renders at all — see {@link computeApiAccess}.
	 * A field rather than a getter so the template binds a value
	 * (`avoid-template-computation`).
	 */
	showApiKeyCard = false;
	// Pack catalog — passed into the Add-credits dialog, which owns the purchase.
	creditPacks: CreditPack[] = [];
	/** Where "Book a demo" goes — the Creator Data API Enterprise tier's own
	 *  Calendly link, the same one `app-enterprise-card` opens. */
	apiDemoUrl: string | null = null;

	private destroy$ = new Subject<void>();

	constructor(
		private subscriptionsState: SubscriptionsStateService,
		private catalogService: CatalogService,
		private cancelService: CancelSubscriptionService,
		private uncancelService: UncancelSubscriptionService,
		private releaseService: ReleaseScheduledChangeService,
		private portalService: OpenBillingPortalSessionService,
		private billingErrors: BillingErrorsService,
		private cancelDialog: CancelSubscriptionDialogService,
		private toasts: LoToastService,
		private apiKeyService: ApiKeyService,
		private creditBalanceService: GetCreditBalanceService,
		private getCreditPacksService: GetCreditPacksService,
		private addCreditsDialog: AddCreditsDialogService,
		private router: Router,
		private anaConnect: AnaChannelConnectService
	) {}

	ngOnInit(): void {
		this.subscriptionsState.loading$
			.pipe(takeUntil(this.destroy$))
			.subscribe(loading => (this.loading = loading));

		// Render from the shared store immediately (it's typically already
		// populated from the app-open load), then refresh on page open.
		combineLatest([
			this.subscriptionsState.subscriptions$,
			this.subscriptionsState.sideUsage$,
			this.catalogService.getProducts()
		])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([subs, side, products]) =>
				this.buildView(subs, side, products)
			);

		this.refresh();
		this.loadApiKeys();
		this.loadCreditBalance();
		this.loadCreditPacks();
		this.loadApiDemoUrl();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	trackByProduct(_: number, card: ProductSubscriptionView): ProductId {
		return card.productId;
	}

	trackByLimit(_: number, limit: { alias: string }): string {
		return limit.alias;
	}

	humanize(alias: string): string {
		// Billing cards label account rows without the "Max " prefix
		// ("Facebook Accounts", not "Max Facebook Accounts") per the design.
		return humanizeAlias(alias).replace(/^Max /, '');
	}

	limitPercent(used: number, cap: number | null): number {
		if (cap === null || cap === 0) {
			return 0;
		}
		return Math.min(100, Math.round((used / cap) * 100));
	}

	/** Display value for a static cap / bool feature entitlement row. */
	entitlementValue(ent: UsageEntitlement): string {
		if (ent.enabled !== null) {
			return ent.enabled ? 'On' : 'Off';
		}
		const fmt = (value: number): string => value.toLocaleString('en-US');
		if (ent.cap === null) {
			return ent.used !== null
				? `${fmt(ent.used)} / unlimited`
				: 'Unlimited';
		}
		return ent.used !== null
			? `${fmt(ent.used)} / ${fmt(ent.cap)}`
			: fmt(ent.cap);
	}

	statusLabel(card: ProductSubscriptionView): string {
		const sub = card.subscription.subscription;
		switch (sub.status) {
			case SubscriptionStatus.ACTIVE:
				return 'Active';
			case SubscriptionStatus.PAST_DUE:
				return 'Payment Failed';
			case SubscriptionStatus.TRIALING_APP:
				return 'Free Trial';
			case SubscriptionStatus.TRIALING_STRIPE:
				return 'Free Trial';
			case SubscriptionStatus.CANCELLED:
				return 'Cancelled';
			case SubscriptionStatus.PENDING:
				return 'Pending';
			default:
				return sub.status;
		}
	}

	/** The status pill's tone: what the subscription's state means for the brand. */
	statusTone(
		card: ProductSubscriptionView
	): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
		switch (card.subscription.subscription.status) {
			case SubscriptionStatus.ACTIVE:
				return 'success';
			case SubscriptionStatus.PAST_DUE:
				return 'danger';
			case SubscriptionStatus.TRIALING_APP:
			case SubscriptionStatus.TRIALING_STRIPE:
				return 'info';
			case SubscriptionStatus.PENDING:
				return 'warning';
			default:
				return 'neutral';
		}
	}

	isAppTrial(card: ProductSubscriptionView): boolean {
		return card.subscription.phase === SubscriptionPhase.APP_TRIAL;
	}

	hasActiveScheduledChange(card: ProductSubscriptionView): boolean {
		const change = card.subscription.scheduled_change;
		return !!change && !change.cancelled_at;
	}

	gotoPlans(card: ProductSubscriptionView): void {
		this.router.navigate(['/user-management/manage-plan'], {
			queryParams: { product_id: card.productId }
		});
	}

	scheduledLabel(card: ProductSubscriptionView): string | null {
		const change = card.subscription.scheduled_change;
		if (!change || change.cancelled_at) {
			return null;
		}
		const date = change.scheduled_for;
		if (change.change_type === ScheduledChangeType.CANCEL) {
			return `Cancels on ${this.formatDate(date)}`;
		}
		if (change.change_type === ScheduledChangeType.DOWNGRADE) {
			return `Downgrades on ${this.formatDate(date)}`;
		}
		return null;
	}

	/**
	 * The "Customer Portal" entry point, which is billing management as a whole —
	 * subscriptions, invoices, payment methods. It deliberately passes no `flow`:
	 * a bare session opens the portal overview, and that overview IS what this
	 * button offers. The buttons that promise one specific thing (add or update a
	 * payment method) pass `flow: 'payment_method_update'` instead — see
	 * {@link PortalSessionRequest}.
	 */
	openCustomerPortal(): void {
		this.portalOpening = true;
		this.portalService
			.open({ return_url: window.location.href })
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: response => {
					this.portalOpening = false;
					if (response.url) {
						this.anaConnect.redirectExternally(
							response.url,
							'billing'
						);
					}
				},
				error: err => {
					this.portalOpening = false;
					this.billingErrors.handle(
						err,
						'Could not open billing portal.'
					);
				}
			});
	}

	cancel(card: ProductSubscriptionView): void {
		if (card.actionInFlight) return;
		// Collect cancellation feedback first, then cancel.
		this.cancelDialog
			.open({ productTitle: card.productTitle })
			.pipe(takeUntil(this.destroy$))
			.subscribe(result => {
				if (!result.confirmed) return;
				this.performCancel(card, result.feedback);
			});
	}

	private performCancel(
		card: ProductSubscriptionView,
		feedback?: CancelSubscriptionFeedback
	): void {
		card.actionInFlight = true;
		const request: CancelRequest = feedback
			? {
					reason: feedback.reason_to_cancel ?? undefined,
					stars_count: feedback.stars_count,
					will_return:
						feedback.will_return === null
							? null
							: feedback.will_return === 'Yes',
					suggestion: feedback.suggestion
			  }
			: {};
		this.cancelService
			.cancel(card.productId, request)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.toasts.success(
						`${card.productTitle} cancellation scheduled.`
					);
					this.refresh();
				},
				error: err => {
					card.actionInFlight = false;
					this.billingErrors.handle(err, 'Cancellation failed.');
				}
			});
	}

	uncancel(card: ProductSubscriptionView): void {
		if (card.actionInFlight) return;
		const changeType = card.subscription.scheduled_change?.change_type;
		const isDowngrade = changeType === ScheduledChangeType.DOWNGRADE;
		const label = isDowngrade ? 'downgrade' : 'cancellation';
		// Downgrade schedules use the dedicated release endpoint; /uncancel
		// only clears a pending cancel and no-ops on a downgrade schedule.
		const request$ = isDowngrade
			? this.releaseService.release(card.productId)
			: this.uncancelService.uncancel(card.productId);
		card.actionInFlight = true;
		request$.pipe(takeUntil(this.destroy$)).subscribe({
			next: () => {
				this.toasts.success(`${card.productTitle} ${label} reverted.`);
				this.refresh();
			},
			error: err => {
				card.actionInFlight = false;
				this.billingErrors.handle(err, `Could not undo ${label}.`);
			}
		});
	}

	/** Re-fetch the shared subscriptions store (updates every consumer). */
	private refresh(): void {
		this.subscriptionsState
			.load()
			.pipe(takeUntil(this.destroy$))
			.subscribe({ error: () => {} });
	}

	private formatDate(iso: string): string {
		try {
			return new Date(iso).toLocaleDateString(undefined, {
				year: 'numeric',
				month: 'short',
				day: 'numeric'
			});
		} catch {
			return iso;
		}
	}

	private buildView(
		subs: ProductSubscription[],
		side: SideUsage,
		products: Product[]
	): void {
		// Outreach (side) usage is a paid-plan entitlement: the backend returns
		// an empty side payload when the user has no allowance (e.g. only on
		// app-trials), so an empty payload alone means Outreach is hidden — and
		// so does a payload carrying nothing but the two retired counters.
		this.outreachCaps = side.side_entitlements;
		this.outreachLimits = side.side_limits.filter(
			limit => !METERED_SIDE_LIMITS.has(limit.alias)
		);
		this.hasSideUsage =
			this.outreachCaps.length > 0 || this.outreachLimits.length > 0;
		const titleByProduct = new Map<number, string>();
		for (const product of products) {
			titleByProduct.set(product.id, product.title);
		}
		this.cards = subs.map(sub => ({
			productId: sub.subscription.product_id,
			productTitle:
				titleByProduct.get(sub.subscription.product_id) ??
				PRODUCT_REGISTRY[sub.subscription.product_id]?.title ??
				sub.plan.title,
			subscription: sub,
			actionInFlight: false
		}));

		this.computeApiUsage(subs);
	}

	// ─── API access ──────────────────────────────────────────────────────

	/**
	 * Precompute the API-request quota from the loaded subscriptions (avoids
	 * recomputing in the template). The public API debits the Discovery
	 * `ai_influencer_analyses_per_month` limit; match by prefix so a future
	 * rename of the alias suffix still resolves.
	 */
	private computeApiUsage(subs: ProductSubscription[]): void {
		let found: UsageLimit | null = null;
		for (const sub of subs) {
			const limit = sub.limits.find(item =>
				item.alias.startsWith('ai_influencer_analyses')
			);
			if (limit) {
				found = limit;
				break;
			}
		}
		this.apiUsage = found;
		if (found && found.cap !== null) {
			this.apiRequestsRemaining = Math.max(0, found.cap - found.used);
			this.apiRequestsPercent = this.limitPercent(found.used, found.cap);
		} else {
			// Unlimited cap or no Discovery limit: no bar, no remaining count.
			this.apiRequestsRemaining = null;
			this.apiRequestsPercent = 0;
		}

		// Monthly credit grant for the Influencer API sub (shown next to the
		// live balance): the static api_credits_grant_per_month entitlement cap.
		let grant: number | null = null;
		for (const sub of subs) {
			const ent = sub.entitlements.find(
				item =>
					item.alias ===
					PlanEntitlementAlias.API_CREDITS_GRANT_PER_MONTH
			);
			if (ent && ent.cap !== null) {
				grant = ent.cap;
				break;
			}
		}
		this.apiCreditsGrant = grant;

		// Does this brand hold the Creator Data API product itself? Matched the
		// way `SubscriptionsStateService.findProductSubscription` matches — the
		// plan alias is always `<productAlias>_<tier>` — and filtered by the
		// statuses that actually grant access, because `past_due` / `pending`
		// rows are returned by `GET /billing` and refused by the backend gate.
		this.hasApiSubscription = subs.some(
			sub =>
				sub.plan.alias.startsWith(ProductAlias.CREATOR_DATA_API) &&
				API_ACCESS_STATUSES.has(sub.subscription.status)
		);
		this.computeApiAccess();
	}

	/**
	 * Whether the API-access card renders, and it is not the same question as
	 * "may this brand mint a key".
	 *
	 * It renders for a brand that holds the Creator Data API product — which can
	 * mint — and ALSO for one that no longer holds it but still has a live key,
	 * because `GET /api-keys` and `DELETE /api-keys/{id}` are deliberately left
	 * open by the backend for exactly that brand: "a brand whose subscription
	 * lapsed must still be able to see its keys and kill a leaked one"
	 * (`app/public_api/auth/router.py`). Withdrawing the card from it would take
	 * away the only way to revoke a credential that still authenticates.
	 *
	 * For everyone else — the platform subscriber that never bought the API —
	 * there is nothing on this card that works, so it does not render at all.
	 */
	private computeApiAccess(): void {
		this.showApiKeyCard =
			this.apiKeyLoaded &&
			(this.hasApiSubscription || !!this.activeApiKey);
	}

	/**
	 * The Creator Data API's Enterprise tier carries the Calendly link the plans
	 * page's own "Talk to us" opens, so "Book a demo" here lands in the same
	 * place rather than inventing a second route to sales. Resolved by ALIAS —
	 * product ids are database-assigned and differ per environment — and falling
	 * back to any enterprise tier's link, since every row on dev and prod today
	 * points at the same booking page.
	 */
	private loadApiDemoUrl(): void {
		combineLatest([
			this.catalogService.getEnterprisePlans(),
			this.catalogService.getProductIdByAlias(
				ProductAlias.CREATOR_DATA_API
			)
		])
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: ([plans, apiProductId]) => {
					const own = plans.find(
						plan =>
							apiProductId !== null &&
							plan.product_id === apiProductId
					);
					this.apiDemoUrl =
						own?.calendly_url ??
						plans.find(plan => !!plan.calendly_url)?.calendly_url ??
						null;
				},
				// A catalogue read that fails leaves no link, and a button that
				// opens nothing is the thing this page is being cleared of.
				error: () => (this.apiDemoUrl = null)
			});
	}

	/** Book a demo of the Creator Data API. */
	bookApiDemo(): void {
		if (this.apiDemoUrl) {
			window.open(this.apiDemoUrl, '_blank');
		}
	}

	/** Live Influencer API credit balance; null hides the credit widget. */
	private loadCreditBalance(): void {
		this.creditBalanceService
			.get()
			.pipe(takeUntil(this.destroy$))
			.subscribe(balance => {
				this.apiCredits = balance.has_subscription ? balance : null;
			});
	}

	private loadCreditPacks(): void {
		this.getCreditPacksService
			.get()
			.pipe(takeUntil(this.destroy$))
			.subscribe(packs => (this.creditPacks = packs));
	}

	openAddCredits(): void {
		this.addCreditsDialog
			.open({
				packs: this.creditPacks,
				balanceCredits: this.apiCredits?.balance_credits ?? null
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe(result => {
				if (result.purchased) {
					// Credits are granted asynchronously by the webhook — poll.
					this.pollCreditBalance();
				}
			});
	}

	private pollCreditBalance(attempt = 0): void {
		this.loadCreditBalance();
		if (attempt < 4) {
			timer(1500)
				.pipe(takeUntil(this.destroy$))
				.subscribe(() => this.pollCreditBalance(attempt + 1));
		}
	}

	private loadApiKeys(): void {
		this.apiKeyLoading = true;
		this.apiKeyService
			.list()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: keys => {
					this.apiKeyLoading = false;
					this.apiKeyLoaded = true;
					this.setActiveKey(keys);
				},
				error: () => {
					this.apiKeyLoading = false;
					this.apiKeyLoaded = true;
					this.computeApiAccess();
				}
			});
	}

	private setActiveKey(keys: ApiKey[]): void {
		const active = keys
			.filter(key => key.status === 'active')
			.sort((a, b) => b.created_at.localeCompare(a.created_at));
		this.activeApiKey = active.length > 0 ? active[0] : null;
		this.apiKeyMasked = this.activeApiKey
			? `lly_live_${this.activeApiKey.prefix}${'•'.repeat(12)}`
			: '';
		this.computeApiAccess();
	}

	generateApiKey(): void {
		this.issueKey(
			this.apiKeyService.create({
				name: 'Dashboard key',
				scopes: DEFAULT_API_SCOPES
			}),
			'API key generated.',
			'Could not generate the API key.'
		);
	}

	regenerateApiKey(): void {
		if (!this.activeApiKey) return;
		this.issueKey(
			this.apiKeyService.rotate(this.activeApiKey.id),
			'API key regenerated — the previous key no longer works.',
			'Could not regenerate the API key.'
		);
	}

	private issueKey(
		request$: ReturnType<ApiKeyService['create']>,
		successMessage: string,
		errorMessage: string
	): void {
		if (this.apiKeyActionInFlight) return;
		this.apiKeyActionInFlight = true;
		request$.pipe(takeUntil(this.destroy$)).subscribe({
			next: created => {
				this.apiKeyActionInFlight = false;
				this.revealedToken = created.token;
				this.toasts.success(successMessage);
				this.loadApiKeys();
			},
			error: err => {
				this.apiKeyActionInFlight = false;
				this.billingErrors.handle(err, errorMessage);
			}
		});
	}

	copyToken(): void {
		if (!this.revealedToken || !navigator.clipboard) return;
		navigator.clipboard.writeText(this.revealedToken).then(
			() => this.toasts.success('API key copied to clipboard.'),
			() => {}
		);
	}

	dismissRevealedToken(): void {
		this.revealedToken = null;
	}
}
